<template>
    <div v-show="!isLoadingAnalysis || !isLoadingScans" class="d-flex flex-column justify-center">
      <v-row>
        <v-col sm="12" md="4" lg="3">
          <div class="sunburst-legend">
             <span class="fs-16">
               Current Selection
             </span>
            <ul v-if="!nodeArray.length">
              <li >
                <div class="d-flex flex-column">
                  <h6 class="mb-1 breadcrumb-heading">{{headings[0]}}</h6>
                  <div class="d-flex flex-row relative">
                    <p>{{currentOrganization.name}}</p>
                    <div class="percentage-box" >
                      100%
                    </div>
                  </div>
                </div>
              </li>
            </ul>
            <ul v-else>
              <li v-for="(node,index) in nodeArray" @click="index==(nodeArray.length -1) ?'':labelClick(node)" :key="node.data.name">
                <div class="d-flex flex-column">
                  <h6 class="mb-1 breadcrumb-heading">{{headings[index]}}</h6>
                  <div class="d-flex flex-row relative">
                    <p>{{node.data.name}}</p>
                    <div class="percentage-box" v-if="(index + 1) == nodeArray.length">
                      {{percentageD}}
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </v-col>
        <v-col sm="12" md="8" lg="9">
          <div id="sequence"></div>
          <div id="sunbody" :style="{width: windowWidth+'px' }" :class="depthClass">
              <div id="sunburst"></div>
          </div>
        </v-col>
      </v-row>
    </div>
</template>
<script>
import * as d3 from 'd3'
import { mapGetters, mapState } from 'vuex'
import { convert } from '@/utils/main_helper'
import exportJsonToCSV from '@/mixins/exportJsonToCSV'

export default {
  props: ['size', 'extension', 'last_modified', 'windowWidth'],
  mixins: [exportJsonToCSV],
  data () {
    return {
      width: this.windowWidth,
      update: false,
      depth: 0,
      headings: ['Company', 'Risk Segment', 'Age', 'Category'],
      nodeArray: [],
      percentageD: '',
      clicked: null,
      rootNode: null
    }
  },
  computed: {
    ...mapGetters(['currentOrganization']),
    ...mapState('discoveryServices', ['isLoadingAnalysis', 'isLoadingScans']),
    ...mapState({
      depthClass () {
        if (this.depth == 0) { return 'depth' } else if (this.depth == 1) { return 'depth1' } else if (this.depth == 2) { return 'depth2' }
      }
    })
  },
  mounted () {
    this.render()
    this.$parent.$on('downloadSunburstCSV', this.downloadSunburstCSV)
  },
  methods: {
    downloadSunburstCSV () {
      const data = this.buildStructure()

      // Convert nested data structure to row-wise data.
      let csvData = []
      const parentAttrs = []
      const totalCount = data.count

      const processLevel = function call (level, parentAttrs, depth) {
        parentAttrs.push(level.name)
        const row = [...parentAttrs]

        for (let i = 0; i < depth; i++) {
          row.push('All')
        }

        const levelCount = parseInt(level.value || level.count)
        const levelVolume = parseInt(level.volume)
        row.push(levelCount)
        row.push(levelVolume)
        row.push(Math.round((levelCount / totalCount) * 100))
        if (row.includes('All') || levelCount != 0 || levelVolume != 0) {
          csvData.push(row)
        }
        if (level.children) {
          level.children.map(level => {
            call(level, parentAttrs, depth - 1)
            parentAttrs.pop()
          })
        }
      }

      processLevel(data, parentAttrs, 3)

      // Custom sorting
      csvData = csvData.sort((a, b) => b[6] - a[6]) // Sort by percentage

      const categoryOrder = ['All']
      csvData = csvData.sort((a, b) => categoryOrder.indexOf(b[3]) - categoryOrder.indexOf(a[3])) // Sort by categories

      const ageOrder = ['All', '< 1 year', '1 - 2 year', '2 - 3 year',
        '3 - 4 year', '4 - 5 year', '5 - 6 year',
        '6 - 7 year', '7 - 8 year', '8 - 9 year',
        '9 - 10 year', '> 10 year']
      csvData = csvData.sort((a, b) => ageOrder.indexOf(a[2]) - ageOrder.indexOf(b[2])) // Sort by categories

      const riskSegmentOrder = ['All', 'Business Continuity', 'Manage & Govern', 'Risk Management']
      csvData = csvData.sort((a, b) => riskSegmentOrder.indexOf(a[1]) - riskSegmentOrder.indexOf(b[1])) // Sort by categories

      const headers = ['Organization', 'Risk Segment', 'Age Segment', 'Category', 'Count', 'Volume', 'Percentage']
      const fileTitle = 'SunburstExport'
      this.exportCSVFile(headers, csvData, fileTitle)
    },
    render () {
      var flag = true
      var leaveEvent = false
      var totalSize = 0
      var breadcrumbArray = []
      var depth = 0
      var clickedNode = null
      if (this.size.length < 1 && this.extension.length < 1 && this.last_modified.length < 1) return
      var data = this.buildStructure()
      var width = this.windowWidth
      var radius = width / 8
      var b = {
        w: 195, h: 40, s: -3, t: 10
      }

      var partition = data => {
        const root = d3.hierarchy(data)
          .sum(d => d.value)
          .sort((a, b) => b.value - a.value)
        return d3.partition()
          .size([2 * Math.PI, root.height + 0.9])
          (root)
      }

      d3.selection.prototype.moveToFront = function () {
        return this.each(function () {
          this.parentNode.appendChild(this)
        })
      }

      var format = d3.format(',d')
      var arc = d3.arc()
        .startAngle(d => d.x0)
        .endAngle(d => d.x1)
        .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
        .padRadius(radius * 2.5)
        .innerRadius(d => d.y0 * calRadius(width, depth))
        .outerRadius(d => Math.max(d.y0 * calRadius(width, depth), d.y1 * calRadius(width, depth) - 1))

      var arcT = d3.arc()
        .startAngle(d => d.x0)
        .endAngle(d => d.x1)
        .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
        .padRadius(radius * 2.5)
        .innerRadius(d => d.y0 * radius)
        .outerRadius(d => Math.max(d.y0 * radius, d.y1 * radius - 1))

      const root = partition(data)
      root.each(d => d.current = d)
      totalSize = root.value
      const order = this.getOrder(root.children[0].value, root.children[1].value, root.children[2].value)
      root.children[order[0]].descendants().map(a => { a.data.theme = 'bc' })
      root.children[order[1]].descendants().map(a => { a.data.theme = 'rm' })
      root.children[order[2]].descendants().map(a => { a.data.theme = 'mg' })
      this.rootNode = root
      this.calculateColors(root)

      if (this.update) { d3.select('svg').remove(); d3.select('svg').selectAll('*').remove() }
      var svg = d3.select('#sunbody svg').node() ? d3.select('#sunbody svg') : d3.select('#sunbody').append('svg')
        .attr('width', this.windowWidth)
        .attr('height', this.windowWidth)
        .attr('id', 'container')
        .style('width', this.windowWidth)
        .style('height', this.windowWidth)
        .style('font', this.windowWidth * 0.011 + 'px' + ' Arial, sans-serif')
        .style('font-weight', 'bold')
        .style('color', 'white')

      const g = svg.append('g')
        .attr('transform', `translate(${width / 2},${width / 2}) rotate(${0})`)

      const path = g.append('g')
        .selectAll('path')
        .data(root.descendants())
        .join('path')
        .attr('fill', d => { return d.color })
        .attr('fill-opacity', d => arcVisible(d.current) ? (d.children ? 1 : 1) : 0)
        .attr('d', d => arc(d.current))
        .on('mouseover', (d) => mouseover(d))

      d3.select('#container').on('mouseleave', (d) => mouseleave(d))

      path.filter(d => d.children)
        .style('cursor', 'pointer')
        .on('click', (p) => clicked(p, 'path'))

      path.append('title')
        .style('font', '15px sans-serif')
        .text(d => {
          const total = root.descendants().filter(n => n.depth == d.depth).map(d => d.value).reduce((a, b) => a + b, 0)
          return `${d.data.name + '\n' + 'Total= ' + format(total) + '\n'}${d.data.name + '\n' + 'Volume= ' + this.volumeNumberFormat(d.data.volume) + '\n'}${'Count= ' + format(d.value) + '\n'}${(((d.value / total) * 100).toFixed(0) < 1 ? '<1' : ((d.value / total) * 100).toFixed(0)) + '%'}`
        })

      const label = g.append('g')
        .attr('pointer-events', 'none')
        .attr('text-anchor', 'middle')
        .style('user-select', 'none')
        .selectAll('text')
        .data(root.descendants())
        .join('text')
        .attr('dy', '0.35em')
        .attr('fill-opacity', d => +labelVisible(d.current))
        .attr('transform', d => { if (d.current.data.name == this.currentOrganization.name) return labelTransform2(d.current); else return labelTransform(d.current) })
        .text(d => (d.data.name.length > 19 && this.depth == 0 ? d.data.name.substring(0, 15) + '...' : d.data.name))
        .style('fill', d => { return this.rgbSum(d.color) < 439 ? '#ffffff' : '#111111' })
        .style('font', d => (d.depth == 0) ? this.windowWidth * 0.015 + 'px' + ' Arial, sans-serif'
          : this.windowWidth * 0.011 + 'px' + ' Arial, sans-serif')
        .style('font-weight', 'bold')

      const label_percent = g.append('g')
        .attr('pointer-events', 'none')
        .attr('text-anchor', 'middle')
        .style('user-select', 'none')
        .selectAll('text')
        .data(root.descendants())
        .join('text')
        .attr('dy', '1.5em')
        .attr('fill-opacity', d => +labelVisible(d.current))
        .attr('transform', d => { if (d.current.data.name == this.currentOrganization.name) return labelTransform2(d.current); else return labelTransform(d.current) })
        .text(d => d.data.name)
        .style('fill', d => { return this.rgbSum(d.color) < 439 ? '#ffffff' : '#111111' })
        .style('font', d => (d.depth == 0) ? this.windowWidth * 0.015 + 'px' + ' Arial, sans-serif'
          : this.windowWidth * 0.011 + 'px' + ' Arial, sans-serif')
        .text(d => {
          const total = root.descendants().filter(n => n.depth == d.depth).map(d => d.value).reduce((a, b) => a + b, 0)
          return `${'\n' + '\n'}  ${'(' + (((d.value / total) * 100).toFixed(0) < 1 ? '<1' : ((d.value / total) * 100).toFixed(0)) + '%' + ')'}`
        })
        .style('font-weight', 'bold')

      const parent = g.append('circle')
        .datum(root)
        .attr('transform', d => labelTransform2(d))
        .attr('r', calRadius(width, depth))
        .attr('fill', 'none')
        .attr('pointer-events', 'all')
        .on('click', (p) => clicked(p, 'parent'))
        .on('mouseover', (d) => mouseover(d))

      // initializeBreadcrumbTrail()

      var clicked = (p, node) => {
        this.setFilterSunburst(p, node)
        parent.datum(p.parent || root)
        root.each(d => d.target = {
          x0: Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
          x1: Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
          y0: Math.max(0, d.y0 - p.depth),
          y1: Math.max(0, d.y1 - p.depth)
        })

        if (node == 'path') {
          this.depth = depth = p.depth
        } else if (depth > 0 && depth != p.depth) {
          this.depth = depth = p.depth
        }

        this.calculateColors(this.rootNode)

        label.filter(d => d.depth != 1)
          .text(d => ((d.data.name.length > 19 && this.depth == 0) ? d.data.name.substring(0, 15) + '...' : d.data.name))
          .style('font', d => (d.depth == 1) ? this.windowWidth * 0.011 + 'px' + ' Arial, sans-serif'
            : (this.depth == 0 ? this.windowWidth * 0.011 + 'px' + ' Arial, sans-serif' : this.windowWidth * 0.013 + 'px' + ' Arial, sans-serif'))

        breadcrumbArray = p.ancestors()
        if (depth == 0) {
          d3.select('#trail')
            .style('visibility', 'hidden')
        } else {
          var percentage = (100 * p.value / totalSize).toPrecision(3)
          var percentageString = percentage + '%'
          if (percentage < 0.1) {
            percentageString = '< 0.1%'
          }
          updateBreadcrumbs(breadcrumbArray.reverse(), percentageString)
        }
        setFlag(false)
        let t = g.transition().duration(750)
        // Transition the data on all arcs, even the ones that aren’t visible,
        // so that if this transition is interrupted, entering arcs will start
        // the next transition from the desired position.
        path.transition(t)
          .on('end', () => setFlag(true))
          .tween('data', d => {
            const i = d3.interpolate(d.current, d.target)
            return t => d.current = i(t)
          })
          .filter(function (d) {
            return +this.getAttribute('fill-opacity') || arcVisible(d.target)
          })
          .attr('fill-opacity', d => arcVisible(d.target) ? (d.children ? 1 : 1) : 0)
          .attrTween('d', d => () => arcT(d.current))

        label_percent.filter(function (d) {
          return +this.getAttribute('fill-opacity') || labelVisible(d.target)
        }).transition(t)
          .attr('fill-opacity', d => +labelVisible(d.target))
          .attrTween('transform', d => () => labelTransformClick(d, p, node))

        label.filter(function (d) {
          return +this.getAttribute('fill-opacity') || labelVisible(d.target)
        }).transition(t)
          .attr('fill-opacity', d => +labelVisible(d.target))
          .attrTween('transform', d => () => labelTransformClick(d, p, node))

        path
          .attr('fill', d => { return d.color })

        label
          .style('fill', d => { return this.rgbSum(d.color) < 439 ? '#ffffff' : '#111111' })
        label_percent
          .style('fill', d => { return this.rgbSum(d.color) < 439 ? '#ffffff' : '#111111' })

        label_percent.filter(d => d == p)
          .style('font', this.windowWidth * 0.015 + 'px' + ' Arial, sans-serif')

        label.filter(d => d == p)
          .style('font', this.windowWidth * 0.015 + 'px' + ' Arial, sans-serif')

        if (node == 'path') {
          clickedNode = p.ancestors()
          clickedNode.pop()
        }

        if (node == 'parent') {
          if (clickedNode.length) {
            const n = clickedNode[0]
            label.filter(d => d == n)
              .style('fill', d => { return this.rgbSum(d.color) < 439 ? '#ffffff' : '#111111' })
              .style('font', d => (d.depth == 1) ? this.windowWidth * 0.0088 + 'px' + ' Arial, sans-serif'
                : this.windowWidth * 0.011 + 'px' + ' Arial, sans-serif')
              .style('font-weight', 'bold')
            label_percent.filter(d => d == n)
              .style('fill', d => { return this.rgbSum(d.color) < 439 ? '#ffffff' : '#111111' })
              .style('font', d => (d.depth == 1) ? this.windowWidth * 0.0088 + 'px' + ' Arial, sans-serif'
                : this.windowWidth * 0.011 + 'px' + ' Arial, sans-serif')
              .style('font-weight', 'bold')
            clickedNode.shift()
          }
        }

        setTimeout(function () {
          t = g.transition().duration(0)
          path.transition(t)
            .on('end', () => setFlag(true))
            .tween('data', d => {
              const i = d3.interpolate(d.current, d.target)
              return t => d.current = i(t)
            })
            .filter(function (d) {
              return +this.getAttribute('fill-opacity') || arcVisible(d.target)
            })
            .attr('fill-opacity', d => arcVisible(d.target) ? (d.children ? 1 : 1) : 0)
            .style('transition-duration', '0.7s')
            .attrTween('d', d => () => arc(d.current))

          label_percent.filter(function (d) {
            return +this.getAttribute('fill-opacity') || labelVisible(d.target)
          }).transition(t)
            .style('transition-duration', '0.7s')
            .attr('fill-opacity', d => +labelVisible(d.target))
            .attrTween('transform', d => () => labelTransformClick(d, p, node, true))

          label.filter(function (d) {
            return +this.getAttribute('fill-opacity') || labelVisible(d.target)
          }).transition(t)
            .style('transition-duration', '0.7s')
            .attr('fill-opacity', d => +labelVisible(d.target))
            .attrTween('transform', d => () => labelTransformClick(d, p, node, true))
        }, 800)

        setTimeout(function () {
          path.each(d => d).style('transition-duration', '0s')
          label_percent.each(d => d).style('transition-duration', '0s')
          label.each(d => d).style('transition-duration', '0s')
        }, 1000)
      }
      this.clicked = clicked

      var setFlag = (f) => {
        flag = f
        if (f && leaveEvent) {
          mouseleave(f)
        }
      }

      var mouseover = (d) => {
        var percentage = ''
        var sequenceArray = d.ancestors()
        sequenceArray.reverse()

        if (this.nodeArray.length - 2 == sequenceArray.length && clickedNode.length) {
          sequenceArray.push(clickedNode[0])
          percentage = (100 * clickedNode[0].value / totalSize).toPrecision(3)
        } else {
          percentage = (100 * d.value / totalSize).toPrecision(3)
        }

        var percentageString = percentage + '%'
        if (percentage < 0.1) {
          percentageString = '< 0.1%'
        }

        updateBreadcrumbs(sequenceArray, percentageString)

        // Fade all the segments.
        d3.selectAll('path')
          .style('opacity', 0.3)

        // Then highlight only those that are an ancestor of the current segment.
        svg.selectAll('path')
          .filter(function (node) {
            return (sequenceArray.indexOf(node) >= 0)
          })
          .style('opacity', 1)
      }

      var updateBreadcrumbs = (nodeArray, percentageString) => {
        this.nodeArray = nodeArray
        this.percentageD = percentageString
      }

      var mouseleave = (d) => {
        // Hide the breadcrumb trail
        if (!flag) {
          leaveEvent = true
          return
        } else {
          leaveEvent = false
        }
        if (depth == 0) {
          this.nodeArray = []
        }
        if (breadcrumbArray.length > 0) {
          var percentage = (100 * breadcrumbArray.reverse()[0].value / totalSize).toPrecision(3)
          var percentageString = percentage + '%'
          if (percentage < 0.1) {
            percentageString = '< 0.1%'
          }
          if (depth != 0) {
            updateBreadcrumbs(breadcrumbArray.reverse(), percentageString)
          }
        }

        // Transition each segment to full opacity and then reactivate it.
        d3.selectAll('path')
          .transition()
          .duration(1000)
          .style('opacity', 1)
      }

      function calRadius (width, depth) {
        if (depth == 0) return width / 8
        else if (depth == 1) return (width / 8) * 1.35
        else if (depth == 2) return (width / 8) * 1.70
      }

      function arcVisible (d) {
        return d.y1 <= 4 && d.y0 >= 0 && d.x1 > d.x0
      }

      function labelVisible (d) {
        return d.y1 <= 4 && d.y0 >= 0 && (d.y1 - d.y0) * (d.x1 - d.x0) > 0.03
      }

      function labelTransform (d, status) {
        const x = (d.x0 + d.x1) / 2 * 180 / Math.PI
        const y = (d.y0 + d.y1) / 2 * (status ? calRadius(width, depth) : radius)
        if (arcT(d).split('A').length == 5) { return `rotate(${x - 180}) translate(${y},0) rotate(${x <= 180 ? 360 : 180})` } else {
          return `rotate(${(d.x1 - d.x0) > 4 ? 0 : (x - 90)}) translate(${y},0) rotate(${x <= 180 ? 360 : 180})`
        }
      }

      function labelTransformClick (dd, p, node, status) {
        const d = dd.current
        const x = (d.x0 + d.x1) / 2 * 180 / Math.PI
        const y = (d.y0 + d.y1) / 2 * (status ? calRadius(width, depth) : radius)
        if (arcT(d).split('A').length == 5 && dd.depth.length != 0 && p.data.name != dd.data.name) { return `rotate(${x - 180}) translate(${y},0) rotate(${x <= 180 ? 0 : 180})` }
        if (node === 'path' && dd.data.name === p.data.name || dd.depth == 0) {
          return `rotate(${x}) translate(0) rotate(${x < 180 ? 0 : 180})`
        } else if (node == 'parent' && dd.data.name === p.data.name) {
          return `rotate(${x}) translate(0) rotate(${x < 180 ? 0 : 180})`
        } else {
          return `rotate(${(d.x1 - d.x0) > 4 ? 0 : (x - 90)}) translate(${y},0) rotate(${x <= 180 ? 360 : 180})`
        }
      }

      function labelTransform2 (d) {
        const x = (d.x0 + d.x1) / 2 * 180 / Math.PI
        // const y = (d.y0 + d.y1) / 2 * calRadius(width, depth)
        return `rotate(${x}) translate(0) rotate(${x < 180 ? 0 : 180})`
      }
    },
    buildStructure () {
      let totalVolume = 0
      let totalCount = 0
      const mg = {
        name: 'Manage & Govern',
        children: [],
        volume: 0,
        count: 0
      }
      const rm = {
        name: 'Risk Management',
        children: [],
        volume: 0,
        count: 0
      }
      const bc = {
        name: 'Business Continuity',
        children: [],
        volume: 0,
        count: 0
      }

      this.last_modified.forEach(element => {
        if (element.label === '< 1' || element.label === '1 - 2' || element.label === '2 - 3') {
          const newE = { ...element }
          bc.children.push({ name: newE.label + ' year', children: [], newE, volume: element.total_size, count: element.total_count })
          totalVolume += parseInt(element.total_size)
          totalCount += parseInt(element.total_count)
        } else if (element.label === '3 - 4' || element.label === '4 - 5' || element.label === '5 - 6') {
          const newE = { ...element }
          mg.children.push({ name: newE.label + ' year', children: [], newE, volume: element.total_size, count: element.total_count })
          totalVolume += parseInt(element.total_size)
          totalCount += parseInt(element.total_count)
        } else if (
          element.label === '6 - 7' ||
          element.label === '> 7' ||
          element.label === '7 - 8' ||
          element.label === '8 - 9' ||
          element.label === '9 - 10' ||
          element.label === '> 10'
        ) {
          const newE = { ...element }
          rm.children.push({ name: newE.label + ' year', children: [], newE, volume: element.total_size, count: element.total_count })
          totalVolume += parseInt(element.total_size)
          totalCount += parseInt(element.total_count)
        }
      })

      bc.children.forEach(element => {
        bc.volume += Number(element.volume)
        bc.count += Number(element.count)
        element.newE.extension_labels && element.newE.extension_labels.forEach(s =>
          element.children.push({ name: s.label, value: s.total_count, volume: s.total_size })
        )
      })
      mg.children.forEach(element => {
        mg.volume += Number(element.volume)
        mg.count += Number(element.count)
        element.newE.extension_labels && element.newE.extension_labels.forEach(s =>
          element.children.push({ name: s.label, value: s.total_count, theme: element.newE.theme, volume: s.total_size })
        )
      })
      rm.children.forEach(element => {
        rm.volume += Number(element.volume)
        rm.count += Number(element.count)
        element.newE.extension_labels && element.newE.extension_labels.forEach(s =>
          element.children.push({ name: s.label, value: s.total_count, theme: element.newE.theme, volume: s.total_size })
        )
      })
      const sbData = {
        name: this.currentOrganization.name,
        children: [{ ...mg }, { ...bc }, { ...rm }],
        count: totalCount,
        volume: totalVolume
      }
      return { ...sbData }
    },
    getNodesByDepthLevel (nodes, depth, theme) {
      if (theme) return nodes.filter(node => node.depth == depth && node.data.theme == theme)
      return nodes.filter(node => node.depth == depth)
    },
    getNodesValues (nodes) {
      return nodes.map(a => { return Number(a.data.volume) }).sort((a, b) => b - a)
    },
    getSibling (node) {
      return node.parent.children
    },
    getD3Color (newarr, value) {
      const color = d3.scaleLinear().domain([newarr[newarr.length - 1], newarr[0]]).range(['#e2eff7', '#212338'])
      return color(value)
    },
    getNodeColor (currentRiskValue, depth, theme) {
      const nodes = this.getNodesByDepthLevel(this.rootNode.descendants(), depth, theme)
      const siblings = this.getNodesValues(nodes)
      const newarr = siblings.map((n, index) => (siblings.length - index) * 100)
      if (newarr.length == 1) { newarr.push(0) }
      currentRiskValue = newarr[siblings.indexOf(currentRiskValue)]
      return this.getD3Color(newarr, currentRiskValue)
    },
    rgbSum (rgb) {
      const nmbrs = rgb.match(/\d+/g)
      return nmbrs.reduce((a, b) => Number(a) + Number(b))
    },
    getOrder (c1, c2, c3) {
      const a = [{ k: 0, value: c1 }, { k: 1, value: c2 }, { k: 2, value: c3 }]
      return a.sort((a, b) => b.value - a.value).map(a => a.k)
    },
    labelClick (p) {
      this.clicked(p, 'parent')
    },
    setFilterSunburst (p, node) {
      if (p.data.name === this.currentOrganization.name) {
        this.$store.commit('discoveryServices/setFilterSunburst', [])
      } else if (p.data.name === 'Business Continuity') {
        this.$store.commit('discoveryServices/setFilterSunburst', ['< 1', '1 - 2', '2 - 3'])
      } else if (p.data.name === 'Manage & Govern') {
        this.$store.commit('discoveryServices/setFilterSunburst', ['3 - 4', '4 - 5', '5 - 6'])
      } else if (p.data.name === 'Risk Management') {
        this.$store.commit('discoveryServices/setFilterSunburst', ['6 - 7', '> 7', '7 - 8', '8 - 9', '9 - 10', '> 10'])
      } else if (p.data.name.includes('year')) {
        const modified = p.data.name.replace(' year', '')
        this.$store.commit('discoveryServices/setFilterSunburst', [modified])
      }
    },
    volumeNumberFormat (number) {
      return convert(number, 'GB', 1000)
    },
    getNode3Color (node) {
      if (this.depth == 0) {
        node.color = this.getNodeColor(node.data.volume, node.depth)
      } else if (this.depth == 1) {
        node.color = this.getNodeColor(node.data.volume, 3, node.data.theme)
      } else {
        const sibling3 = this.getSibling(node).map(a => { return Number(a.data.volume) }).sort((a, b) => b - a)
        const newarr3 = sibling3.map((n, index) => (sibling3.length - index) * 100)
        if (newarr3.length == 1) {
          newarr3.push(0)
        }
        const volume = newarr3[sibling3.indexOf(node.data.volume)]
        node.color = this.getD3Color(newarr3, volume)
      }
    },
    calculateColors (root) {
      root.descendants().forEach(node => {
        if (node.depth == 3) {
          this.getNode3Color(node)
        }
      })
      root.descendants().forEach(node => {
        if (node.depth == 2) {
          node.color = node.children ? this.getColorsAvg(node.children.map(n => n.color)) : 'rgba(33, 35, 56)'
        }
      })
      root.descendants().forEach(node => {
        if (node.depth == 1) {
          node.color = node.children ? this.getColorsAvg(node.children.map(n => n.color)) : 'rgba(33, 35, 56)'
        }
      })
      root.color = root.children ? this.getColorsAvg(root.children.map(n => n.color)) : 'rgba(33, 35, 56)'
    },
    getColorsAvg (colors) {
      let red = 0; let green = 0; let blue = 0
      colors.forEach(color => {
        const nmbrs = color.match(/\d+/g)
        red += Number(nmbrs[0])
        green += Number(nmbrs[1])
        blue += Number(nmbrs[2])
      })
      red = Math.ceil(red / colors.length)
      green = Math.ceil(green / colors.length)
      blue = Math.ceil(blue / colors.length)
      return `rgb(${red},${green},${blue})`
    }
  },
  watch: {
    last_modified () {
      this.update = true
      this.depth = 0
      this.nodeArray = []
      this.percentageD = ''
      this.clicked = null
      this.render()
    }
  }
}
</script>
<style lang="scss" >
 #trail{
   margin-bottom: -20px;
 }
 .depth{
   margin-top: -20px !important;
 }
 .depth2{
   margin-top: -100px !important;
 }
 .sunburst-legend{
   ul {
      list-style-type: none;
      padding: 0;
      margin-top: 18px;
      li {
          cursor: pointer;
          position: relative;
          display: flex;
          align-items: center;
          font-size: 20px;
          line-height: 1;
          text-decoration: none;
          color: $setting-black-color;
          // padding: 9px 0;
          transition: 0.3s;
          &:before {
            display: inline-block;
            content: '';
            width: 15px;
            height: 15px;
            margin-top: -49px;
            background: $secondary-color;
            border: 1px solid $secondary-color;
            border-radius: 50px;
            margin-right: 16px;
            transition: 0.3s;
            z-index: 1;
          }
          &:after {
            content: "";
            position: absolute;
            top: 18px;
            left: 7px;
            width: 1px;
            margin-top: -10px;
            height: 65px;
            background: $black-color;
            z-index: 0;
          }
        &:last-child:after {
            content: none;
          }
        &:last-child:before {
            display: inline-block;
            content: '';
            width: 21px;
            height: 21px;
            background: $secondary-color;
            border: 1px solid $secondary-color;
            border-radius: 50px;
            margin-right: 12px;
            margin-left: -3px;
            transition: 0.3s;
            z-index: 1
          }
      }
   }
   p {
        margin-bottom: 42px !important;
        white-space: nowrap;
     }
   .breadcrumb-heading {
     color: $black-color;
     text-transform: uppercase;
      font-weight: 700;
     letter-spacing: 0.4px;

   }
   .percentage-box{
     background: $secondary-color;
     padding: 2px 20px 0px 7px;
     height: 19px;
     color: $black-color;
     border-radius: 15px;
     position: absolute;
     top: -17px;
     right: -78px;
     font-size: 10px !important;
     font-weight: 700;
     text-align: center;
     &:before {
        display: inline-block;
        content: '';
        position: relative;
        top:1px;
        left: -12px;
        transition: 0.3s;
        width: 0;
        height: 0;
        transform: scaleY(0.7);
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-right:6px solid $secondary-color;
      }
   }
 }
 .no-wrap{
  font-size: 14px !important;
  margin-top: 4px;
 }
 @media only screen and (max-width: 1920px) {
  .sunburst-legend ul li{
    font-size: 16px;
  }
  .percentage-box{
    font-size: 16px !important;
  }
  .no-wrap{
    font-size: 14px !important;
    margin-top: 4px;
  }
 }
 @media only screen and (max-width: 1620px) {
  .sunburst-legend ul li{
    font-size: 14px;
  }
  .percentage-box{
    font-size: 16px !important;
  }
  .no-wrap{
    font-size: 12px !important;
    margin-top: 4px;
  }
 }
 @media only screen and (max-width: 1370px) {
  .sunburst-legend ul li{
    font-size: 12px;
  }
  .percentage-box{
    font-size: 16px !important;
  }
  .no-wrap{
    font-size: 10px !important;
    margin-top: 4px;
  }
 }

</style>
